import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { votersFor, votesFromVoters } from "../api/attendance";
import { useDateOptionsForEvent } from "../api/date-options";
import { VoteAnswer, useGetEvent } from "../api/events-api";
import AvatarCircle from "../components/Avatar";
import { FitText } from "../components/dss/FitText";
import { Icon } from "../components/dss/Icon";
import { Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { Eyebrow } from "../components/dss/Primitives";
import { SegmentedTabs } from "../components/dss/SegmentedTabs";
import { Skeleton } from "../components/dss/Skeleton";
import { useLogeMembers } from "../api/user-api";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

// These dates only came out Danish because some other page happened to set
// the locale first; this screen states it for itself.
dayjs.locale(da);

type SortingOption = "popularity" | "date";

const VotableEventResultPage = () => {
  const { eventId } = useParams();
  const { data: event } = useGetEvent(eventId);
  const navigate = useNavigate();
  const location = useLocation();
  // Which tab sent us here, if a tab did — the pill picks up from there.
  const cameFromTab = (location.state as { from?: string } | null)?.from;
  const [selectedSorting, setSelectedSorting] = useState<SortingOption>("popularity");
  const { member, hasRole } = useSupabaseAuth();
  const { data: dateOptions = [] } = useDateOptionsForEvent(eventId);
  const { data: members = [] } = useLogeMembers();

  const animateIn = useEnterAnimation(!event);

  const results = useMemo(() => {
    const withTallies = dateOptions.map((d) => {
      // The host isn't asked, so a stray vote of theirs doesn't count.
      const votes = votesFromVoters(d.votes, event?.host ?? null);
      return {
        ...d,
        yesIds: votes.filter((v) => v.answer === VoteAnswer.YES).map((v) => v.user_id),
        maybeIds: votes.filter((v) => v.answer === VoteAnswer.MAYBE).map((v) => v.user_id),
      };
    });
    return [...withTallies].sort((a, b) =>
      selectedSorting === "date"
        ? new Date(a.date).getTime() - new Date(b.date).getTime()
        : b.yesIds.length - a.yesIds.length || b.maybeIds.length - a.maybeIds.length,
    );
  }, [dateOptions, event?.host, selectedSorting]);

  // The bars are read against everyone who could have answered, not against
  // the best-performing date. Four yeses out of six possible should look like
  // four out of six, whether or not any other date did better.
  const voterCount = Math.max(1, votersFor(members, event?.host ?? null).length);

  const votedCount = useMemo(
    () => new Set(results.flatMap((r) => [...r.yesIds, ...r.maybeIds])).size,
    [results],
  );

  // "Vinder" marks the date that was actually chosen, not the one currently
  // ahead. While the vote is open the leader can still change, and calling it
  // the winner reads as a decision nobody has made yet — the bars already
  // show who's in front.
  const winnerId = useMemo(() => {
    if (event?.status !== "confirmed" || !event.date) return undefined;
    return results.find((r) => dayjs(r.date).isSame(event.date, "day"))?.id;
  }, [event?.status, event?.date, results]);

  if (!event) {
    return (
      <div className="mx-auto max-w-md bg-paper pt-safe min-h-app">
        <div className="px-7 pt-2 pb-3">
          <Skeleton className="w-14 h-3" />
          <Skeleton className="w-32 h-3 mt-4" />
          <Skeleton className="w-full h-10 mt-1.5" />
          <Skeleton className="w-full h-10 mt-4 rounded-lg" />
        </div>
        <div className="px-7 pt-3 flex items-center justify-between">
          <Skeleton className="w-20 h-4" />
          <Skeleton className="w-28 h-8 rounded-lg" />
        </div>
        <div className="px-7 pt-4 pb-6 flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-baseline gap-3">
              <Skeleton className="w-14 h-10" />
              <div className="flex-1 flex flex-col gap-2">
                <Skeleton className="w-2/3 h-4" />
                <Skeleton className="w-full h-2.5 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe min-h-app flex flex-col">
      <div className="px-7 pt-2 pb-3">
        <button
          onClick={() => navigate("/")}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>Kalender</span>
        </button>
        {/* Says what the bars are measured against, and replaces a caption
            that claimed the page had just updated whether it had or not. */}
        <Eyebrow className="mt-4">
          RESULTAT · {votedCount} AF {voterCount} HAR STEMT
        </Eyebrow>
        <FitText className="font-display text-[40px] leading-[0.92] tracking-tight font-extrabold mt-1.5 uppercase">
          {event.title}
        </FitText>
        {/* The host has no ballot to switch to, so they get a line saying why
            rather than a tab that would bounce them straight back here. */}
        {member?.id === event.host ? (
          <div className="mt-4 font-mono text-[10px] tracking-wide uppercase text-ink3 flex items-center gap-2">
            <Icon name="circle" size={7} />
            <span>Du er vært — du skal ikke stemme</span>
          </div>
        ) : (
          <SegmentedTabs
            segments={[
              { key: "vote", label: "Stem" },
              { key: "result", label: "Resultat" },
            ]}
            className="mt-4"
            activeKey="result"
            enterFrom={cameFromTab}
            // `intent` marks this as someone deliberately going to change
            // their vote, so the ballot doesn't bounce them straight back
            // here for having already voted. Replace, so the two tabs stay
            // one step; `from` hands the pill's position over with them.
            onSelect={(key) =>
              key === "vote" &&
              navigate(`/vote/${eventId}`, {
                replace: true,
                state: { intent: "vote", from: "result" },
              })
            }
          />
        )}
        {(hasRole("ADMIN") || member?.id === event.host) && (
          <button
            onClick={() => navigate(`/vote/${eventId}/edit`)}
            className="mt-3 font-mono text-[10px] tracking-wide uppercase text-bilberry font-semibold"
          >
            Redigér afstemning
          </button>
        )}
      </div>

      <div className="px-7 pt-3 flex items-center justify-between">
        <Eyebrow>Sortér efter</Eyebrow>
        {/* Unlike the tab strip above, this one switches state in place — so
            it keeps its own pill and simply slides it, no handover needed. */}
        <SegmentedTabs
          variant="compact"
          segments={[
            { key: "popularity", label: "Flest" },
            { key: "date", label: "Dato" },
          ]}
          activeKey={selectedSorting}
          onSelect={(key) => setSelectedSorting(key as SortingOption)}
        />
      </div>

      <Stagger enabled={animateIn} className="px-7 pt-4 pb-6 flex flex-col gap-4">
        {results.map((r) => {
          const yesPct = (r.yesIds.length / voterCount) * 100;
          const maybePct = (r.maybeIds.length / voterCount) * 100;
          const isWinner = r.id === winnerId;
          return (
            <StaggerItem key={r.id}>
              <div className="flex items-baseline gap-3">
                <div className="w-14">
                  <div className="font-mono text-[9.5px] tracking-wide text-ink2 font-medium uppercase">
                    {dayjs(r.date).format("MMM")}
                  </div>
                  <div
                    className={`font-display font-extrabold text-[30px] leading-[0.92] tracking-tight tabular-nums ${
                      isWinner ? "text-vote-yes" : ""
                    }`}
                  >
                    {dayjs(r.date).format("DD")}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-display text-sm font-bold uppercase">
                    {dayjs(r.date).format("dddd D. MMMM")}
                  </div>
                  <div className="font-mono text-[10.5px] text-ink3 mt-0.5 tracking-wide">
                    {r.yesIds.length} JA · {r.maybeIds.length} MÅSKE
                  </div>
                </div>
                {isWinner && (
                  <div className="font-mono text-[9px] tracking-wide uppercase font-semibold text-paper bg-vote-yes rounded px-2 py-1">
                    VINDER
                  </div>
                )}
              </div>
              <div className="ml-[68px] mt-2">
                <div className="h-2.5 bg-paper2 border border-line-soft rounded flex overflow-hidden">
                  <div className="bg-vote-yes" style={{ width: `${yesPct}%` }} />
                  <div className="bg-vote-maybe" style={{ width: `${maybePct}%` }} />
                </div>
                <div className="flex -space-x-1 mt-2">
                  {[...r.yesIds, ...r.maybeIds].slice(0, 8).map((id) => (
                    <AvatarCircle
                      key={id}
                      userId={id}
                      size="xs"
                      showName={false}
                      bgColor={r.yesIds.includes(id) ? "green" : "yellow"}
                    />
                  ))}
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  );
};

export default VotableEventResultPage;
